import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  DEFAULT_LANG,
  LANGS,
  getLang,
  setLang as setLangState,
  subscribe,
  type Lang,
} from "./state";
import { TRANSLATIONS } from "./translations";

type Params = Record<string, string | number>;

interface I18nValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string, params?: Params) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, name) =>
    name in params ? String(params[name]) : `{${name}}`,
  );
}

function translate(lang: Lang, key: string, params?: Params): string {
  const dict = TRANSLATIONS[lang] ?? TRANSLATIONS[DEFAULT_LANG];
  const value = dict[key] ?? TRANSLATIONS[DEFAULT_LANG][key] ?? key;
  return interpolate(value, params);
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(getLang());

  useEffect(() => {
    document.documentElement.lang = lang;
    return subscribe(setLang);
  }, [lang]);

  const value = useMemo<I18nValue>(
    () => ({
      lang,
      setLang: setLangState,
      t: (key, params) => translate(lang, key, params),
    }),
    [lang],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}

export function useT(): (key: string, params?: Params) => string {
  return useI18n().t;
}

export function LanguageSwitcher() {
  const { lang, setLang, t } = useI18n();
  return (
    <div className="lang-switcher">
      <label htmlFor="lang-select">{t("language.label")}</label>
      <select
        id="lang-select"
        value={lang}
        onChange={(e) => setLang(e.target.value as Lang)}
      >
        {LANGS.map((l) => (
          <option key={l} value={l}>
            {t(`language.${l}`)}
          </option>
        ))}
      </select>
    </div>
  );
}
